import * as React from 'react';

import { RowTitle } from 'common/components';
import usePluggableEntityTableElements from 'hooks/usePluggableEntityTableElements';
import { useModalContext } from 'common/contexts';
import type { ColumnRenderers } from 'components/common/EntityDataTable';
import type { EventProceduresEntityType } from 'security-app/hooks/api/eventProceduresAPI.types';

import DescriptionCell from '../DescriptionCell';

const TitleCell = ({ eventProcedure }: { eventProcedure: EventProceduresEntityType }) => {
  const [selectedProcedureId, setSelectedProcedureId] = React.useState(undefined);
  const { setModal, setEntity } = useModalContext();

  const onClickProcedure = () => {
    setSelectedProcedureId(eventProcedure.id);
    setEntity(eventProcedure);
    setModal('SHOW-EVENT-PROCEDURE-DETAILS');
  };

  React.useEffect(() => {
    if (eventProcedure.id === selectedProcedureId) setEntity(eventProcedure);
  }, [eventProcedure, selectedProcedureId, setEntity]);

  return <RowTitle onClick={onClickProcedure}>{eventProcedure?.title || 'No title set'}</RowTitle>;
};

// eslint-disable-next-line import/prefer-default-export
export const useColumnRenderers = () => {
  const { pluggableColumnRenderers }: any = usePluggableEntityTableElements<EventProceduresEntityType>(
    null,
    'event_procedure',
  );

  const collectionsRenderer = React.useMemo(
    () =>
      pluggableColumnRenderers?.collections
        ? pluggableColumnRenderers
        : {
            collections: {
              staticWidth: 130,
              renderCell: () => <i>Unauthorized</i>,
            },
          },
    [pluggableColumnRenderers],
  );

  const columnRenderers: ColumnRenderers<EventProceduresEntityType> = React.useMemo(
    () => ({
      attributes: {
        title: {
          width: 0.3,
          renderCell: (_: string, eventProcedure: EventProceduresEntityType) => (
            <TitleCell eventProcedure={eventProcedure} />
          ),
        },
        description: {
          width: 0.7,
          renderCell: (_: string, eventProcedure: EventProceduresEntityType) => (
            <DescriptionCell mdValue={eventProcedure.description} />
          ),
        },
        ...collectionsRenderer,
      },
    }),
    [collectionsRenderer],
  );

  return { columnRenderers };
};
